import { usePromotions } from "../context/PromotionContext";
import React, { useContext, useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  PiArrowsClockwise,
  PiHeart,
  PiScales,
  PiPackage,
  PiGift,
  PiTag,
  PiTruck,
  PiUser,
  PiUserCircle,
} from "react-icons/pi";
import { BsBasket } from "react-icons/bs";
import { RiCloseLine, RiLogoutCircleLine, RiMenuLine } from "react-icons/ri";
import { CartContext } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { useCustomer } from "../context/CustomerContext";
import { useWishlist } from "../context/WishlistContext";
import { useComparison } from "../context/ComparisonContext";
import SearchBar from "../components/SearchBar";
import AuthCustomerModal from "../components/AuthCustomerModal";
import RegisterCustomerModal from "../components/RegisterCustomerModal";
import { CATEGORY_BUTTONS } from "../config/animalsConfig";
import {
  CATEGORY_NAV_ITEMS,
  CUSTOMER_SERVICE_DELIVERY_PAYMENT_PATH,
  CUSTOMER_SERVICE_RETURNS_EXCHANGE_PATH,
  PROMOTIONS_PATH,
} from "../config/navigation";
import {
  filterProductsByCategory,
  getProductFilterValues,
} from "../utils/filterUtils";
import {
  compareKindLabels,
  getKindPresentation,
} from "../utils/categoryKindPresentation";
import { buildCategoryKindPath } from "../utils/categoryKindRoutes";
import styles from "./Header.module.css";

const SEARCH_PLACEHOLDER = "Пошук товарів для ваших улюбленців...";

const CATEGORY_LABELS = {
  dogs: "Собаки",
  cats: "Коти",
  birds: "Птахи",
  rodents: "Гризуни",
  fish: "Риби",
  reptiles: "Рептилії",
};

const CATEGORY_IMAGE_BY_SLUG = CATEGORY_BUTTONS.reduce((acc, item) => {
  acc[item.slug] = item.image;
  return acc;
}, {});

const animalCategoryLinks = CATEGORY_NAV_ITEMS.map((item) => ({
  ...item,
  label: CATEGORY_LABELS[item.slug] || item.label,
  image: CATEGORY_IMAGE_BY_SLUG[item.slug] || item.image,
  Icon: PiGift,
  isAnimalCategory: true,
}));

const desktopCategoryLinks = animalCategoryLinks;
const saleAnimalCategoryLinks = animalCategoryLinks.map((item) => ({
  ...item,
  label: item.menuLabel || item.label,
}));

const toProductsArray = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.items)) return data.items;
  if (data && Array.isArray(data.products)) return data.products;
  return [];
};

const buildHeaderCategoryKindPath = (item, kind) => {
  if (item?.slug) {
    return buildCategoryKindPath(item.slug, kind);
  }

  const params = new URLSearchParams();

  if (!item?.path && item?.query) {
    params.set("category", item.query);
  }

  params.set("kind", kind);

  return `${item?.path || "/products"}?${params.toString()}`;
};

const SALE_FILTER_PARAM = "discounted";
const LEGACY_SALE_SORT_VALUE = "only-discounted";

const isSaleFilterEnabled = (search) => {
  const params = new URLSearchParams(search || "");
  const discountedValue = String(params.get(SALE_FILTER_PARAM) || "")
    .trim()
    .toLowerCase();
  const sortValue = String(params.get("sort") || "").trim();

  return (
    discountedValue === "1" ||
    discountedValue === "true" ||
    discountedValue === "yes" ||
    sortValue === LEGACY_SALE_SORT_VALUE
  );
};

const headerNavigationLinks = [
  {
    label: "Акції та знижки",
    path: PROMOTIONS_PATH,
    Icon: PiTag,
    opensAnimalSaleMenu: true,
  },
  {
    label: "Доставка та оплата",
    path: CUSTOMER_SERVICE_DELIVERY_PAYMENT_PATH,
    Icon: PiTruck,
  },
];

const mobileDrawerLinks = [
  ...headerNavigationLinks,
  {
    label: "Обмін та повернення",
    path: CUSTOMER_SERVICE_RETURNS_EXCHANGE_PATH,
    Icon: PiArrowsClockwise,
  },
];

const hasProminentHeaderIcon = (item) =>
  item.largeIcon ||
  item.path.includes("discounted=1") ||
  item.path === PROMOTIONS_PATH;

const Header = () => {
  const { visible: promotionsVisible } = usePromotions();
  const { cartItems, getTotalCartPrice } = useContext(CartContext);
  const { wishlist = [], clearUserWishlistState } = useWishlist();
  const { comparisonCount = 0 } = useComparison();
  const totalCartPrice = getTotalCartPrice();

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCatalogOpen, setIsMobileCatalogOpen] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isDesktopSaleMenuOpen, setIsDesktopSaleMenuOpen] = useState(false);
  const [isMobileSaleMenuOpen, setIsMobileSaleMenuOpen] = useState(false);
  const [activeCategorySlug, setActiveCategorySlug] = useState(null);
  const [activeDrawerCategory, setActiveDrawerCategory] = useState(null);
  const [categoryKindMap, setCategoryKindMap] = useState({});
  const { isLoggedIn: isAdminLoggedIn, logout: adminLogout } = useAuth();
  const { customer, logout: customerLogout } = useCustomer();

  const location = useLocation();
  const navigate = useNavigate();
  const cartCount = cartItems.length;
  const wishlistCount = wishlist.length;
  const wishlistPath = customer ? "/cabinet/wishlist" : "/wishlist";
  const isHomePage = location.pathname === "/";
  const shouldRenderDesktopCategories = isHomePage;
  const backendUrl = process.env.REACT_APP_API_URL || "http://localhost:5000";

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsMobileCatalogOpen(false);
    setIsMobileSaleMenuOpen(false);
    setIsDesktopSaleMenuOpen(false);
    setActiveCategorySlug(null);
    setActiveDrawerCategory(null);
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => {
    let isCancelled = false;

    const fetchCategoryKinds = async () => {
      try {
        const response = await axios.get(`${backendUrl}/api/products`, {
          params: { limit: 1200 },
          withCredentials: false,
        });
        const data = toProductsArray(response.data);

        if (isCancelled) return;

        const nextMap = desktopCategoryLinks.reduce((acc, item) => {
          const categoryProducts = filterProductsByCategory(data, item.query);
          const kinds = new Set();

          categoryProducts.forEach((product) => {
            getProductFilterValues(product, "kinds").forEach((kind) => {
              kinds.add(kind);
            });
          });

          acc[item.slug] = Array.from(kinds).sort(compareKindLabels);
          return acc;
        }, {});

        setCategoryKindMap(nextMap);
      } catch (error) {
        if (!isCancelled) {
          console.error("Failed to load category kind filters:", error);
          setCategoryKindMap({});
        }
      }
    };

    fetchCategoryKinds();

    return () => {
      isCancelled = true;
    };
  }, [backendUrl]);

  useEffect(() => {
    if (!shouldRenderDesktopCategories) {
      setActiveCategorySlug(null);
    }
  }, [shouldRenderDesktopCategories]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileMenuOpen]);

  const handleUserClick = () => {
    if (customer) {
      navigate("/cabinet/profile");
      return;
    }

    localStorage.setItem(
      "lastVisitedPath",
      location.pathname + location.search,
    );
    setIsAuthModalOpen(true);
  };

  const handleMobileUserClick = () => {
    closeMobileMenu();
    handleUserClick();
  };

  const handleMobileRegisterClick = () => {
    localStorage.setItem(
      "lastVisitedPath",
      location.pathname + location.search,
    );
    closeMobileMenu();
    setIsAuthModalOpen(false);
    setIsRegisterModalOpen(true);
  };

  const handleLogout = async () => {
    clearUserWishlistState();
    if (customer) await customerLogout();
    if (isAdminLoggedIn) await adminLogout();
    setIsMobileMenuOpen(false);
    navigate("/");
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setIsMobileCatalogOpen(false);
    setIsMobileSaleMenuOpen(false);
    setActiveDrawerCategory(null);
  };

  const openMobileMenu = () => {
    setIsMobileCatalogOpen(false);
    setIsMobileSaleMenuOpen(false);
    setActiveDrawerCategory(null);
    setIsMobileMenuOpen(true);
  };

  const getSaleCategoryPath = (item) => {
    return item?.slug
      ? `${PROMOTIONS_PATH}/category/${item.slug}`
      : PROMOTIONS_PATH;
  };

  const isSaleNavigationActive =
    location.pathname === PROMOTIONS_PATH ||
    location.pathname.startsWith(`${PROMOTIONS_PATH}/`) ||
    location.pathname === "/sale" ||
    (location.pathname.startsWith("/products") &&
      isSaleFilterEnabled(location.search));

  const isPathActive = (path) => {
    if (path === "/") return location.pathname === "/" && !location.hash;
    if (path.includes("#")) {
      const [pathname, hash] = path.split("#");
      return location.pathname === pathname && location.hash === `#${hash}`;
    }
    if (path === PROMOTIONS_PATH || path.includes("discounted=1")) {
      return isSaleNavigationActive;
    }
    if (path.includes("?q=")) {
      const query = decodeURIComponent(path.split("?q=")[1] || "");
      return (
        location.pathname === "/products" &&
        decodeURIComponent(location.search).toLowerCase().includes(query)
      );
    }
    return location.pathname === path;
  };

  const scrollToCurrentHashTarget = (path) => {
    if (!path.includes("#")) return;

    const [pathname, hash] = path.split("#");
    if (location.pathname !== pathname || location.hash !== `#${hash}`) {
      return;
    }

    window.requestAnimationFrame(() => {
      document.getElementById(hash)?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  };

  const handleHeaderNavLinkClick = (path) => {
    scrollToCurrentHashTarget(path);
  };

  const handleDrawerNavLinkClick = (path) => {
    closeMobileMenu();
    scrollToCurrentHashTarget(path);
  };

  const isWishlistActive = customer
    ? isPathActive("/cabinet/wishlist")
    : isPathActive("/wishlist");

  const isHeaderNavigationActive = (path) => {
    if (path === "/products") return location.pathname.startsWith("/products");
    return isPathActive(path);
  };

  const handleCategoryBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setActiveCategorySlug(null);
    }
  };

  const handleMobileCatalogToggle = () => {
    setIsMobileSaleMenuOpen(false);
    setIsMobileCatalogOpen((isOpen) => {
      if (isOpen) {
        setActiveDrawerCategory(null);
      }

      return !isOpen;
    });
  };

  const handleDrawerCategoryToggle = (slug) => {
    setActiveDrawerCategory((currentSlug) =>
      currentSlug === slug ? null : slug
    );
  };

  const handleDesktopSaleMenuBlur = (event) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsDesktopSaleMenuOpen(false);
    }
  };

  const handleMobileSaleMenuToggle = () => {
    setIsMobileCatalogOpen(false);
    setActiveDrawerCategory(null);
    setIsMobileSaleMenuOpen((isOpen) => !isOpen);
  };

  const renderKindFilterLink = (
    item,
    kind,
    {
      className = styles.categoryKindDropdownItem,
      imageClassName = styles.categoryKindImage,
      onClick,
    } = {}
  ) => {
    const kindPresentation = getKindPresentation(kind);

    return (
      <Link
        key={`${item.slug}-${kind}`}
        to={buildHeaderCategoryKindPath(item, kind)}
        className={className}
        role="menuitem"
        onClick={onClick}
      >
        <img
          src={kindPresentation.image}
          alt={kindPresentation.alt}
          className={imageClassName}
          loading="lazy"
        />
        <span>{kind}</span>
      </Link>
    );
  };

  const userButtonTitle = customer
    ? `Кабінет: ${customer.firstName} ${customer.lastName}`
    : "Реєстрація або авторизація";
  const profileLabel = customer
    ? `${customer.firstName || ""} ${customer.lastName || ""}`.trim() ||
      "Профіль"
    : "Профіль";
  const drawerProfileLabel = customer
    ? `${customer.lastName || ""} ${customer.firstName || ""}`.trim() ||
      profileLabel
    : "Профіль";

  const formattedCartTotal = new Intl.NumberFormat("uk-UA", {
    maximumFractionDigits: 0,
  }).format(totalCartPrice || 0);

  const renderLogo = (mobile = false) => (
    <Link
      to="/"
      className={`${styles.logoLink} ${mobile ? styles.mobileLogoLink : ""}`}
      aria-label="MyZooShop"
    >
      <img
        src={`${process.env.PUBLIC_URL}/Cat_in_box_logo_none.svg`}
        alt="Логотип MyZooShop"
        className={styles.logoImage}
      />
      <span className={styles.logoCopy}>
        <span className={styles.logoText}>MyZooShop</span>
        <span className={styles.logoSubtitle}>зоомагазин</span>
      </span>
    </Link>
  );

  const renderBadge = (count) =>
    count > 0 ? <span className={styles.actionBadge}>{count}</span> : null;

  const renderDesktopActions = () => (
    <div className={styles.headerActions}>
      {headerNavigationLinks.filter((link) => !link.opensAnimalSaleMenu || promotionsVisible).map(({ label, path, Icon, opensAnimalSaleMenu }) => {
        if (opensAnimalSaleMenu) {
          return (
            <div
              key={`${label}-${path}-desktop-sale`}
              className={styles.headerActionMenuGroup}
              onMouseEnter={() => setIsDesktopSaleMenuOpen(true)}
              onMouseLeave={() => setIsDesktopSaleMenuOpen(false)}
              onFocus={() => setIsDesktopSaleMenuOpen(true)}
              onBlur={handleDesktopSaleMenuBlur}
            >
              <Link
                to={path}
                className={`${styles.headerAction} ${styles.headerNavAction} ${
                  isSaleNavigationActive ? styles.activeIconLink : ""
                }`}
                aria-expanded={isDesktopSaleMenuOpen}
                aria-haspopup="menu"
                aria-controls="desktop-sale-categories-menu"
                aria-current={isSaleNavigationActive ? "page" : undefined}
              >
                <span className={styles.actionIcon}>
                  <Icon aria-hidden="true" />
                </span>
                <span className={styles.actionLabel}>{label}</span>
              </Link>

              {isDesktopSaleMenuOpen && (
                <div
                  id="desktop-sale-categories-menu"
                  className={styles.saleCategoryDropdown}
                  role="menu"
                >
                  {saleAnimalCategoryLinks.map((categoryItem) => (
                    <Link
                      key={`${categoryItem.slug}-desktop-sale`}
                      to={getSaleCategoryPath(categoryItem)}
                      className={styles.saleCategoryDropdownItem}
                      role="menuitem"
                      onClick={() => setIsDesktopSaleMenuOpen(false)}
                    >
                      {categoryItem.image ? (
                        <img
                          src={categoryItem.image}
                          alt=""
                          className={styles.saleCategoryImage}
                          loading="lazy"
                          aria-hidden="true"
                        />
                      ) : (
                        <categoryItem.Icon aria-hidden="true" />
                      )}
                      <span>{categoryItem.label}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        }

        const isActive = isHeaderNavigationActive(path);

        return (
          <Link
            key={path}
            to={path}
            className={`${styles.headerAction} ${styles.headerNavAction} ${
              isActive ? styles.activeIconLink : ""
            }`}
            aria-current={isActive ? "page" : undefined}
            onClick={() => handleHeaderNavLinkClick(path)}
          >
            <span className={styles.actionIcon}>
              <Icon aria-hidden="true" />
            </span>
            <span className={styles.actionLabel}>{label}</span>
          </Link>
        );
      })}

      <button
        className={styles.headerAction}
        onClick={handleUserClick}
        type="button"
        title={userButtonTitle}
        aria-label={userButtonTitle}
      >
        <span className={styles.actionIcon}>
          {customer ? <PiUserCircle aria-hidden="true" /> : <PiUser aria-hidden="true" />}
        </span>
        <span className={styles.actionLabel}>{profileLabel}</span>
      </button>

      <Link
        to={wishlistPath}
        className={`${styles.headerAction} ${
          isWishlistActive ? styles.activeIconLink : ""
        }`}
        title="Обране"
        aria-label={`Обране, товарів: ${wishlistCount}`}
      >
        <span className={styles.actionIcon}>
          <PiHeart aria-hidden="true" />
          {renderBadge(wishlistCount)}
        </span>
        <span className={styles.actionLabel}>Обране</span>
      </Link>
      <Link
        to="/comparison"
        className={`${styles.headerAction} ${
          isPathActive("/comparison") ? styles.activeIconLink : ""
        }`}
        title="Порівняння товарів"
        aria-label={`Порівняння, товарів: ${comparisonCount}`}
      >
        <span className={styles.actionIcon}>
          <PiScales aria-hidden="true" />
          {renderBadge(comparisonCount)}
        </span>
        <span className={styles.actionLabel}>Порівняння</span>
      </Link>

      <Link
        to="/cart"
        className={`${styles.headerAction} ${styles.cartAction} ${
          isPathActive("/cart") ? styles.activeIconLink : ""
        }`}
        title="Ваш кошик"
        aria-label={`Кошик, товарів: ${cartCount}`}
      >
        <span className={styles.actionIcon}>
          <BsBasket aria-hidden="true" />
          {renderBadge(cartCount)}
        </span>
        <span className={styles.actionTextGroup}>
          {cartCount > 0 ? (
            <span className={styles.cartTotal}>{formattedCartTotal} ₴</span>
          ) : (
            <span className={styles.actionLabel}>Кошик</span>
          )}
        </span>
      </Link>

      {(customer || isAdminLoggedIn) && (
        <button
          className={`${styles.headerAction} ${styles.logoutAction}`}
          onClick={handleLogout}
          type="button"
          title="Вийти з облікового запису"
          aria-label="Вийти з облікового запису"
        >
          <span className={styles.actionIcon}>
            <RiLogoutCircleLine aria-hidden="true" />
          </span>
          <span className={styles.actionLabel}>Вийти</span>
        </button>
      )}
    </div>
  );

  const renderMobileCartLink = () => (
    <Link
      to="/cart"
      className={`${styles.mobileIconButton} ${styles.mobileCartButton} ${
        isPathActive("/cart") ? styles.activeMobileIcon : ""
      }`}
      title="Ваш кошик"
      aria-label={
        cartCount > 0
          ? `Кошик, товарів: ${cartCount}, сума: ${formattedCartTotal} ₴`
          : "Кошик"
      }
    >
      <span className={styles.mobileCartIconWrap}>
        <BsBasket aria-hidden="true" />
        {renderBadge(cartCount)}
      </span>
      {cartCount > 0 && (
        <span className={styles.mobileCartTotal}>{formattedCartTotal} ₴</span>
      )}
    </Link>
  );

  return (
    <header className={styles.headerContainer}>
      <div className={styles.desktopHeader}>
        <div className={styles.desktopHeaderBar}>
          <div className={styles.headerInner}>
            <div className={styles.headerLogo}>{renderLogo()}</div>

            <div className={styles.headerSearch}>
              <SearchBar placeholder={SEARCH_PLACEHOLDER} />
            </div>

            {renderDesktopActions()}
          </div>
        </div>

        {shouldRenderDesktopCategories && (
          <nav
            className={`${styles.headerCategories} ${
              isHomePage ? styles.stickyCategories : ""
            }`}
            aria-label="Категорії товарів"
          >
            {desktopCategoryLinks.map((item) => {
              const { label, path, Icon, image, featured, isAnimalCategory } =
                item;
              const kindItems = categoryKindMap[item.slug] || [];
              const isDropdownOpen =
                activeCategorySlug === item.slug && kindItems.length > 0;

              return (
                <div
                  key={`${label}-${path}`}
                  className={styles.categoryNavGroup}
                  onMouseEnter={() => setActiveCategorySlug(item.slug)}
                  onMouseLeave={() => setActiveCategorySlug(null)}
                  onFocus={() => setActiveCategorySlug(item.slug)}
                  onBlur={handleCategoryBlur}
                >
                  <Link
                    to={path}
                    className={`${styles.categoryNavItem} ${
                      isAnimalCategory ? styles.animalCategoryNavItem : ""
                    } ${
                      isPathActive(path) ? styles.activeCategoryNavItem : ""
                    } ${featured ? styles.featuredCategoryNavItem : ""}`}
                    aria-current={isPathActive(path) ? "page" : undefined}
                    aria-haspopup={kindItems.length > 0 ? "menu" : undefined}
                    aria-expanded={
                      kindItems.length > 0 ? isDropdownOpen : undefined
                    }
                  >
                    {image ? (
                      <img
                        src={image}
                        alt=""
                        className={styles.categoryNavImage}
                        aria-hidden="true"
                      />
                    ) : (
                      <Icon
                        className={
                          hasProminentHeaderIcon(item)
                            ? styles.largeCategoryNavIcon
                            : ""
                        }
                        aria-hidden="true"
                      />
                    )}
                    <span>{label}</span>
                  </Link>

                  {isDropdownOpen && (
                    <div className={styles.categoryKindDropdown} role="menu">
                      {kindItems.map((kind) =>
                        renderKindFilterLink(item, kind, {
                          onClick: () => setActiveCategorySlug(null),
                        })
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>
        )}
      </div>

      <div className={styles.mobileHeader}>
        <div className={styles.mobileHeaderTop}>
          <button
            className={styles.mobileIconButton}
            onClick={openMobileMenu}
            type="button"
            aria-label="Відкрити меню"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            <RiMenuLine aria-hidden="true" />
          </button>

          <div className={styles.mobileLogoSlot}>{renderLogo(true)}</div>

          <button
            className={styles.mobileIconButton}
            onClick={handleMobileUserClick}
            type="button"
            aria-label={userButtonTitle}
            title={userButtonTitle}
          >
            {customer ? <PiUserCircle aria-hidden="true" /> : <PiUser aria-hidden="true" />}
          </button>

          <Link
            to={wishlistPath}
            className={`${styles.mobileIconButton} ${
              isWishlistActive ? styles.activeMobileIcon : ""
            }`}
            title="Обране"
            aria-label={`Обране, товарів: ${wishlistCount}`}
          >
            <PiHeart aria-hidden="true" />
            {renderBadge(wishlistCount)}
          </Link>

          <Link
            to="/comparison"
            className={`${styles.mobileIconButton} ${
              isPathActive("/comparison") ? styles.activeMobileIcon : ""
            }`}
            title="Порівняння товарів"
            aria-label={`Порівняння, товарів: ${comparisonCount}`}
          >
            <PiScales aria-hidden="true" />
            {renderBadge(comparisonCount)}
          </Link>
          {renderMobileCartLink()}
        </div>

        <div className={styles.mobileSearchWrap}>
          <SearchBar mobile placeholder={SEARCH_PLACEHOLDER} />
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className={styles.mobileMenuOverlay} onClick={closeMobileMenu}>
          <nav
            id="mobile-navigation"
            className={styles.mobileMenuDrawer}
            aria-label="Мобільна навігація"
            onClick={(event) => event.stopPropagation()}
          >
            <div className={styles.drawerTop}>
              {renderLogo(true)}
              <button
                onClick={closeMobileMenu}
                className={styles.drawerCloseButton}
                aria-label="Закрити меню"
                type="button"
              >
                <RiCloseLine aria-hidden="true" />
              </button>
            </div>

            <div className={styles.drawerMenuList}>
              <button
                className={`${styles.drawerMenuItem} ${styles.drawerCatalogButton}`}
                onClick={handleMobileCatalogToggle}
                type="button"
                aria-expanded={isMobileCatalogOpen}
                aria-controls="mobile-catalog-panel"
              >
                <span className={styles.drawerMenuLabel}>
                  <PiPackage aria-hidden="true" />
                  <span>Каталог товарів</span>
                </span>
                <span
                  className={`${styles.drawerMenuArrow} ${
                    isMobileCatalogOpen ? styles.drawerMenuArrowOpen : ""
                  }`}
                  aria-hidden="true"
                >
                  ›
                </span>
              </button>

              {isMobileCatalogOpen && (
                <div
                  id="mobile-catalog-panel"
                  className={styles.drawerCatalogPanel}
                >
                  {desktopCategoryLinks.map((item) => {
                    const { label, path, image, Icon } = item;
                    const kindItems = categoryKindMap[item.slug] || [];
                    const isDrawerCategoryOpen =
                      activeDrawerCategory === item.slug;
                    const kindsPanelId = `mobile-catalog-${item.slug}-kinds`;

                    return (
                      <div
                        key={`${label}-${path}-catalog`}
                        className={styles.drawerCatalogGroup}
                      >
                        <button
                          type="button"
                          className={`${styles.drawerCatalogItem} ${
                            isDrawerCategoryOpen
                              ? styles.activeDrawerCatalogItem
                              : ""
                          }`}
                          onClick={() => {
                            if (kindItems.length > 0) {
                              handleDrawerCategoryToggle(item.slug);
                              return;
                            }

                            closeMobileMenu();
                            navigate(path);
                          }}
                          aria-expanded={
                            kindItems.length > 0
                              ? isDrawerCategoryOpen
                              : undefined
                          }
                          aria-controls={
                            kindItems.length > 0 ? kindsPanelId : undefined
                          }
                        >
                          <span className={styles.drawerCatalogLabel}>
                            {image ? (
                              <img
                                src={image}
                                alt=""
                                className={styles.drawerCatalogImage}
                                aria-hidden="true"
                              />
                            ) : (
                              <Icon aria-hidden="true" />
                            )}
                            <span>{label}</span>
                          </span>
                          <span
                            className={`${styles.drawerCatalogArrow} ${
                              isDrawerCategoryOpen
                                ? styles.drawerCatalogArrowOpen
                                : ""
                            }`}
                            aria-hidden="true"
                          >
                            ›
                          </span>
                        </button>

                        {isDrawerCategoryOpen && kindItems.length > 0 && (
                          <div
                            id={kindsPanelId}
                            className={styles.drawerKindList}
                            role="menu"
                          >
                            {kindItems.map((kind) =>
                              renderKindFilterLink(item, kind, {
                                className: styles.drawerKindItem,
                                imageClassName: styles.drawerKindImage,
                                onClick: closeMobileMenu,
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {mobileDrawerLinks.filter((link) => !link.opensAnimalSaleMenu || promotionsVisible).map((item) => {
                const { label, path, Icon, opensAnimalSaleMenu } = item;

                if (opensAnimalSaleMenu) {
                  return (
                    <div
                      key={`${label}-${path}-drawer-sale`}
                      className={styles.drawerSaleGroup}
                    >
                      <button
                        type="button"
                        className={`${styles.drawerMenuItem} ${
                          isSaleNavigationActive ? styles.activeDrawerMenuItem : ""
                        }`}
                        onClick={handleMobileSaleMenuToggle}
                        aria-expanded={isMobileSaleMenuOpen}
                        aria-controls="mobile-sale-categories-panel"
                      >
                        <span className={styles.drawerMenuLabel}>
                          <Icon
                            className={
                              hasProminentHeaderIcon(item)
                                ? styles.largeDrawerMenuIcon
                                : ""
                            }
                            aria-hidden="true"
                          />
                          <span>{label}</span>
                        </span>
                        <span
                          className={`${styles.drawerMenuArrow} ${
                            isMobileSaleMenuOpen ? styles.drawerMenuArrowOpen : ""
                          }`}
                          aria-hidden="true"
                        >
                          вЂє
                        </span>
                      </button>

                      {isMobileSaleMenuOpen && (
                        <div
                          id="mobile-sale-categories-panel"
                          className={styles.drawerSaleCategoryList}
                          role="menu"
                        >
                          {saleAnimalCategoryLinks.map((categoryItem) => (
                            <Link
                              key={`${categoryItem.slug}-mobile-sale`}
                              to={getSaleCategoryPath(categoryItem)}
                              className={styles.drawerSaleCategoryItem}
                              role="menuitem"
                              onClick={closeMobileMenu}
                            >
                              {categoryItem.image ? (
                                <img
                                  src={categoryItem.image}
                                  alt=""
                                  className={styles.drawerSaleCategoryImage}
                                  loading="lazy"
                                  aria-hidden="true"
                                />
                              ) : (
                                <categoryItem.Icon aria-hidden="true" />
                              )}
                              <span>{categoryItem.label}</span>
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }

                return (
                  <Link
                    key={`${label}-${path}-drawer`}
                    to={path}
                    className={`${styles.drawerMenuItem} ${
                      isPathActive(path) ? styles.activeDrawerMenuItem : ""
                    }`}
                    onClick={() => handleDrawerNavLinkClick(path)}
                  >
                    <span className={styles.drawerMenuLabel}>
                      <Icon
                        className={
                          hasProminentHeaderIcon(item)
                            ? styles.largeDrawerMenuIcon
                            : ""
                        }
                        aria-hidden="true"
                      />
                      <span>{label}</span>
                    </span>
                    <span className={styles.drawerMenuArrow} aria-hidden="true">
                      ›
                    </span>
                  </Link>
                );
              })}
            </div>

            {customer ? (
              <div className={styles.drawerBottom}>
                <button
                  className={styles.drawerBottomAction}
                  onClick={handleMobileUserClick}
                  type="button"
                >
                  <span className={styles.drawerMenuLabel}>
                    <PiUserCircle aria-hidden="true" />
                    <span>{drawerProfileLabel}</span>
                  </span>
                </button>

                <button
                  className={`${styles.drawerBottomAction} ${styles.drawerLogoutAction}`}
                  onClick={handleLogout}
                  type="button"
                >
                  <span className={styles.drawerMenuLabel}>
                    <RiLogoutCircleLine aria-hidden="true" />
                    <span>Вийти</span>
                  </span>
                </button>
              </div>
            ) : (
              <div className={styles.drawerBottom}>
                <button
                  className={styles.drawerBottomAction}
                  onClick={handleMobileUserClick}
                  type="button"
                >
                  <span className={styles.drawerMenuLabel}>
                    <PiUser aria-hidden="true" />
                    <span>Авторизація</span>
                  </span>
                </button>
                <button
                  className={styles.drawerBottomAction}
                  onClick={handleMobileRegisterClick}
                  type="button"
                >
                  <span className={styles.drawerMenuLabel}>
                    <PiUserCircle aria-hidden="true" />
                    <span>Реєстрація</span>
                  </span>
                </button>
              </div>
            )}
          </nav>
        </div>
      )}

      {isAuthModalOpen && (
        <AuthCustomerModal
          onClose={() => setIsAuthModalOpen(false)}
          onRegisterClick={() => {
            setIsAuthModalOpen(false);
            setIsRegisterModalOpen(true);
          }}
        />
      )}
      {isRegisterModalOpen && (
        <RegisterCustomerModal
          onClose={() => setIsRegisterModalOpen(false)}
          onLoginClick={() => {
            setIsRegisterModalOpen(false);
            setIsAuthModalOpen(true);
          }}
        />
      )}
    </header>
  );
};

export default Header;
